import { Descriptions as AntdDescriptions, Col, Collapse, Row, Alert, Space } from "antd";
import { useContext, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Descriptions from "../../general/antd/descriptions";
import PageDetails from "../../general/details";
import { useNavigate, useParams, Link } from "react-router-dom";
import Spin from "../../general/antd/spin";
import AppContext from "../../../context/app/context";
import OrderContext from "../../../context/order/context";
import OrderContextProvider from "../../../context/order/provider";
import Tag from "../../general/antd/tag";
import {
  getOrderStatus,
  getOrderStatusColor,
  getOrderStyle,
  getOrderTypeColor,
  getorderSchedulingTypeStyle,
  getorderSchedulingTypeColor,
  getOrderKindName,
  getOrderKindColor,
  getDeliveryModeName,
  getDeliveryModeColor,
} from "../../../models/order/enum";
import { TripStatus } from "../../../models/order/response";
import { durationBetweenDatesByDays } from "../../../utils/helpers/functions";
import { ImCheckmark, ImCross } from "react-icons/im";
import OrderStatusContextProvider from "../../../context/order-status/provider";
import OrderHistoryContextProvider from "../../../context/order-history/provider";
import OrderHistoryTable from "../order-history-table";
import OrderStatusTable from "../order-status-table";
import moment from "moment";
import OldImage from "../../form-components/old-image";
import ProductsOrderTable from "../products-order";
import RefreshBtn from "../../general/table-components/actions/refresh-btn";
import WorkerHistoryTable from "../../delivery-worker/delivery-worker-history";
import DeliveryWorkerHistoryContextProvider from "../../../context/delivery-worker-history/provider";
import Marker from "../../general/map/marker";
import StoreContextProvider from "../../../context/store/provider";
import { Button, Tooltip, Modal, theme } from "antd";
import { PlusOutlined, CheckOutlined, WarningOutlined, EyeOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { Permissions } from "../../../utils/Permissions/Permissions";
import { successNotification } from "../../../utils/helpers/notification";
import TripBadge from "../trip-badge";

const { Panel } = Collapse;
const { useToken } = theme;
interface Props { }

const Order: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { token } = useToken();
  const { direction, screenSize } = useContext(AppContext);
  const { details, actions, loading } = useContext(OrderContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [isRefreshed, setIsRefreshed] = useState(false);
  const userPermissions = localStorage.getItem("permisionStoroge")?.split(",") || [];
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  // Check if order is in an active trip (PENDING or ACTIVE status)
  const isInActiveTrip = useMemo(() => {
    if (!details?.tripId || !details?.tripStatus) return false;
    return details.tripStatus === TripStatus.PENDING || details.tripStatus === TripStatus.ACTIVE;
  }, [details?.tripId, details?.tripStatus]);

  useEffect(() => {
    const getDetails = async () => {
      id && (await actions.getDetails(Number(id)));
    };
    getDetails();
  }, [direction, isRefreshed]);
  return (
    <>
      <PageDetails
        title={t("details")}
        subtitle={t("client")}
        loading={loading.includes('details')}
        onRefresh={() => actions.getDetails(Number(id))}
      >
        {loading.includes("details") ? (
          <Spin />
        ) : (
          <>
            {/* Order Protection Warning Banner */}
            {isInActiveTrip && (
              <Alert
                message={t("order_in_active_trip")}
                description={
                  <Space direction="vertical" size="small">
                    <span>{t("order_in_active_trip_warning")}</span>
                    <Space>
                      <span style={{ fontWeight: 500 }}>{t("trip_id")}: {details?.tripNumber || details?.tripId}</span>
                      <Link to={`/trips/details/${details?.tripId}`}>
                        <Button type="primary" size="small" icon={<EyeOutlined />}>
                          {t("view_trip")}
                        </Button>
                      </Link>
                    </Space>
                  </Space>
                }
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Location Changed Alert */}
            {details?.locationChanged && (
              <Alert
                message={t("location_changed")}
                description={
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    <span>{t("location_changed_description")}</span>
                    <Descriptions size="small" column={screenSize === "laptopOrDesktop" ? 2 : 1} bordered>
                      <AntdDescriptions.Item label={t("browsing_store")}>
                        {details?.browsingStore?.name ?? "-"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("delivery_store")}>
                        {details?.deliveryStore?.name ?? "-"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("browsing_total_price")}>
                        {details?.browsingTotalPrice?.toLocaleString() ?? "-"} {t("currency")}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("delivery_total_price")}>
                        {details?.deliveryTotalPrice?.toLocaleString() ?? "-"} {t("currency")}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("price_difference")} span={2}>
                        <span style={{
                          color: (details?.priceDifference ?? 0) > 0 ? token.colorError : token.colorSuccess,
                          fontWeight: 600,
                          fontSize: "16px"
                        }}>
                          {(details?.priceDifference ?? 0) > 0 ? "+" : ""}{details?.priceDifference?.toLocaleString() ?? "0"} {t("currency")}
                        </span>
                      </AntdDescriptions.Item>
                    </Descriptions>
                  </div>
                }
                type="warning"
                showIcon
                icon={<EnvironmentOutlined />}
                style={{ marginBottom: 16 }}
              />
            )}
            <Row gutter={[8, 8]}>
            <Col span={24}>
              <Collapse>
                <Panel
                  header={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                      <span>{t("details")}</span>
                      {userPermissions.includes(Permissions.CAN_CONFIRM_ORDER_PAYMENT) &&
                        details?.status === 8 && // COMPLETED_AND_PAID
                        !details?.paymentReceivedBy && (
                          <Button
                            type="primary"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmModalVisible(true);
                            }}
                            loading={loading.includes("confirm_payment")}
                          >
                            {t("confirm_payment_receipt")}
                          </Button>
                        )}
                    </div>
                  }
                  key={1}
                >
                  <Descriptions size="small" column={screenSize == "laptopOrDesktop" ? 2 : 1} bordered>
                    <AntdDescriptions.Item label={t("id")}>
                      {details?.id}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("number_id")}>
                      {details?.number}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("is_express_delivery")}>
                      {<Tag
                        title={t(getOrderKindName(details?.isExpressDelivery))}
                        color={getOrderKindColor(details?.isExpressDelivery)}
                      />}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("quantityProcessed")}>
                      {details?.quantityProcessed ? (
                        <ImCheckmark color="green" />
                      ) : (
                        <ImCross color="red" />
                      )}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("client")}>
                      {details?.client?.firstName ?? "-"}{" "}
                      {details?.client?.lastName ?? "-"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("client_mobile")}>
                      {details?.client?.cleanMobile ?? "-"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("client_address")}>
                      {details?.clientAddress ?? "-"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("client")}>
                      {details?.client?.firstName ?? "-"}{" "}
                      {details?.client?.lastName ?? "-"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("last_completed_order_date")}>
                      {details?.client?.lastCompletedOrderDate === null ? "-" : moment(details?.client?.lastCompletedOrderDate).format("YYYY-MM-DD HH:mm:ss")}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("days_form_last_order")}>
                      {details?.client?.lastCompletedOrderDate === null ? "-" : durationBetweenDatesByDays(moment(details?.client?.lastCompletedOrderDate), moment(details?.createdAt))}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("delivery_worker")}>
                      {details?.driver?.firstName ?? "-"}{" "}
                      {details?.driver?.lastName ?? "-"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("delivery_worker_mobile")}>
                      {details?.driver?.cleanMobile ?? "-"}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("investor")}>
                      {details?.investor?.firstName ?? "-"}{" "}
                      {details?.investor?.lastName ?? "-"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("store")}>
                      {details?.store?.name ?? "-"}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("store_have_recipe")}>
                      {details?.StoreHaveRecipe?.name ?? "-"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("order_scheduling_type")}>
                      {
                        <Tag
                          title={getorderSchedulingTypeStyle(
                            details?.orderSchedulingType
                          )}
                          color={getorderSchedulingTypeColor(
                            details?.orderSchedulingType
                          )}
                        />
                      }
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("schedule_time")}>
                      {details?.scheduleTime
                        ? moment(details?.scheduleTime).format(
                          "YYYY-MM-DD HH:mm:ss"
                        )
                        : "--"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("request_sourse_type")}>
                      {
                        <Tag
                          title={getOrderStyle(details?.type)}
                          color={getOrderTypeColor(details?.type)}
                        />
                      }
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("current_order_status")}>
                      {
                        <Tag
                          title={getOrderStatus(details?.status)}
                          color={getOrderStatusColor(details?.status)}
                        />
                      }
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("trip_info")}>
                      <TripBadge
                        tripId={details?.tripId}
                        tripNumber={details?.tripNumber}
                        tripStatus={details?.tripStatus}
                      />
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("delivery_mode")}>
                      <Tag
                        title={t(getDeliveryModeName(details?.deliveryMode))}
                        color={getDeliveryModeColor(details?.deliveryMode)}
                      />
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("date_and_time")}>
                      {moment(details?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("final_price")}>
                      {details?.finalPrice ?? "-"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("reason_cancel")}>
                      {details?.reasonCancel ?? "-"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("emergency")}>
                      {details?.isEmergency ? (
                        <ImCheckmark color="green" />
                      ) : (
                        <ImCross color="red" />
                      )}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("order_broadcast_area")}>
                      {details?.orderBroadcastArea ? (
                        <ImCheckmark color="green" />
                      ) : (
                        <ImCross color="red" />
                      )}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("order_broadcast_city")}>
                      {details?.orderBroadcastCity ? (
                        <ImCheckmark color="green" />
                      ) : (
                        <ImCross color="red" />
                      )}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("product_broadcast_area")}>
                      {details?.productBroadcastArea ? (
                        <ImCheckmark color="green" />
                      ) : (
                        <ImCross color="red" />
                      )}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("product_broadcast_city")}>
                      {details?.productBroadcastCity ? (
                        <ImCheckmark color="green" />
                      ) : (
                        <ImCross color="red" />
                      )}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("is_recipe")}>
                      {details?.isRecipe ? (
                        <ImCheckmark color="green" />
                      ) : (
                        <ImCross color="red" />
                      )}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("do_not_contact")}>
                      {!details?.contact ? (
                        <ImCheckmark color="green" />
                      ) : (
                        <ImCross color="red" />
                      )}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("not_ringing_bell")}>
                      {!details?.ringingBell ? (
                        <ImCheckmark color="green" />
                      ) : (
                        <ImCross color="red" />
                      )}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("expanded_search")}>
                      {details?.selectedStore ? (
                        <ImCross color="red" />
                      ) : (
                        <ImCheckmark color="green" />
                      )}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item
                      label={t("price_of_products_from_pharmacy")}
                    >
                      {details?.totalPrice}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("total_price_on_client")}>
                      {details?.totalPriceOnClient}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("products_yield")}>
                      {details?.productYield}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("price_delivery_service")}>
                      {details?.priceDeliveryService}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item
                      label={t("price_delivery_Service_on_client")}
                    >
                      {details?.priceDeliveryServiceOnClient}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item label={t("price_delivery_yield")}>
                      {details?.priceDeliveryYield}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("discountCode")}>
                      {details?.discountCode?.code}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("discountValueProduct")}>
                      {details?.discountCodeValueForProduct}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("discountValueDelivery")}>
                      {details?.discountCodeValueForDelivery}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("distance")}>
                      {details?.distance}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item
                      label={t("applicable_discount_code")}
                    >
                      {details?.discountCode?.name}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("finish_date_time")}>
                      {details?.finishDatetime
                        ? moment(details?.finishDatetime).format(
                          "YYYY-MM-DD HH:mm:ss"
                        )
                        : "--"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("start_datetime_driver")}>
                      {details?.startDatetimeDriver
                        ? moment(details?.startDatetimeDriver).format(
                          "YYYY-MM-DD HH:mm:ss"
                        )
                        : "--"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("recipe_as_text")}>
                      {details?.recipeAsText ?? "-"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("recipes_images")} span={2}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                          gap: "7px",
                        }}
                      >
                        {details?.recipes?.map((img) => {
                          return <OldImage src={img?.url} preview />;
                        })}
                      </div>
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item
                      label={t("customer_evaluation_of_the_service")}
                      span={2}
                    >
                      {details?.rating}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item
                      label={t("client_rate_note")}
                      span={2}
                    >
                      {details?.clientRateNote}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item
                      label={t("client_note_additional")}
                      span={2}
                    >
                      {details?.clientNote}
                    </AntdDescriptions.Item>

                    <AntdDescriptions.Item
                      label={t("client_note_delivery")}
                      span={2}
                    >
                      {details?.clientNoteDelivery}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("order_notes")} span={2}>
                      {details?.notes?.map((note, index) => (
                        <div key={note?.id}>
                          <span> {index + 1}-</span> {note?.text} : ({" "}
                          {note?.noteForNote} ).
                        </div>
                      )) ?? "-"}
                    </AntdDescriptions.Item>
                    <AntdDescriptions.Item label={t("managment_note")} span={2}>
                      {details?.note}
                    </AntdDescriptions.Item>

                    {/* Payment Confirmation Section */}
                    <AntdDescriptions.Item label={t("payment_status")} span={2}>
                      {details?.paymentReceivedBy ? (
                        <div style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          padding: "12px",
                          backgroundColor: token.colorSuccessBg,
                          border: `1px solid ${token.colorSuccessBorder}`,
                          borderRadius: token.borderRadius
                        }}>
                          <Tag title={t("payment_received")} color="green" />
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "8px" }}>
                            <span style={{ fontSize: "14px", fontWeight: "500", color: token.colorText }}>
                              <strong>{t("payment_received_by_accountant")}:</strong>{" "}
                              {details.paymentReceivedBy.fullName || `${details.paymentReceivedBy.firstName} ${details.paymentReceivedBy.lastName}`}
                            </span>
                          <span style={{
                              fontSize: "14px",
                              color: token.colorTextSecondary,
                              unicodeBidi: "plaintext"
                          }}>
                          <strong>{t("payment_received_at")}:</strong>{" "}
                              {moment(details.paymentReceivedAt).format("YYYY-MM-DD HH:mm:ss")}
                          </span>
                          </div>
                        </div>
                      ) : (
                        <Tag title={t("payment_not_received")} color="gray" />
                      )}
                    </AntdDescriptions.Item>
                  </Descriptions>
                </Panel>
              </Collapse>
            </Col>
            <Col span={24}>
              <Collapse>
                <Panel header={t("location")} key={1}>
                  <Marker
                    width="100%"
                    name={
                      details?.client?.firstName +
                      " " +
                      details?.client?.lastName
                    }
                    draggable={false}
                    location={details?.location}
                  />
                </Panel>
              </Collapse>
            </Col>
            <Col span={24}>
              <Collapse defaultActiveKey={1}>
                <Panel
                  header={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>{t("products_order")}</span>
                      {userPermissions.includes(Permissions.CAN_ADMIN_ADD_ORDER_PRODUCT) && (
                        <Tooltip title={isInActiveTrip ? t("cannot_modify_order_in_trip") : undefined}>
                          <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            disabled={isInActiveTrip}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isInActiveTrip) {
                                navigate(`/orders/add-products/${id}`, {
                                  state: {
                                    orderNumber: details?.number,
                                    storeId: details?.store?.id,
                                    existingProductIds: details?.orderProducts?.map((p: any) => p.product.id) || [],
                                  },
                                });
                              }
                            }}
                            aria-label={t("add_product")}
                          >
                            {t("add_product")}
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  }
                  key={1}
                >
                  <ProductsOrderTable
                    data={details?.orderProducts}
                    orderId={Number(id)}
                    disableMutations={isInActiveTrip}
                    onRefresh={() => actions.getDetails(Number(id))}
                  />
                </Panel>
              </Collapse>
            </Col>

            <Col span={24}>
              <Collapse>
                <Panel header={t("order_history")} key={1}>
                  <OrderHistoryTable />
                </Panel>
              </Collapse>
            </Col>
            <Col span={24}>
              <Collapse>
                <Panel header={t("order_status")} key={1}>
                  <OrderStatusTable />
                </Panel>
              </Collapse>
            </Col>
            <Col span={24}>
              <Collapse>
                <Panel header={t("history_driver")} key={1}>
                  <WorkerHistoryTable orderAssent={true} />
                </Panel>
              </Collapse>
            </Col>
          </Row>
          </>
        )}

        {/* Payment Confirmation Modal */}
        <Modal
          title={t("confirm_payment_receipt")}
          open={confirmModalVisible}
          onCancel={() => setConfirmModalVisible(false)}
          onOk={async () => {
            await actions.confirmPayment(Number(id));
            successNotification(t("payment_confirmed_successfully"));
            setConfirmModalVisible(false);
          }}
          okText={t("confirm")}
          cancelText={t("cancel")}
          confirmLoading={loading.includes("confirm_payment")}
          width={550}
        >
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "16px 0"
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px",
              backgroundColor: token.colorFillAlter,
              borderRadius: token.borderRadius,
              border: `1px solid ${token.colorBorder}`
            }}>
              <span style={{ fontSize: "14px", color: token.colorTextSecondary }}>{t("order_number")}:</span>
              <span style={{ fontSize: "16px", fontWeight: "600", color: token.colorTextSecondary }}>{details?.number}</span>
            </div>

            <div style={{
              padding: "16px",
              backgroundColor: token.colorSuccessBg,
              border: `1px solid ${token.colorSuccessBorder}`,
              borderRadius: token.borderRadius
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                <span style={{ fontSize: "14px", color: token.colorTextSecondary }}>{t("total_products_price")}:</span>
                <span style={{ fontSize: "15px", fontWeight: "500", color: token.colorText }}>
                  {details?.totalPrice ? Number(details.totalPrice).toFixed(2) : "0.00"} {t("currency")}
                </span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                <span style={{ fontSize: "14px", color: token.colorTextSecondary }}>{t("delivery_fee")}:</span>
                <span style={{ fontSize: "15px", fontWeight: "500", color: token.colorText }}>
                  {details?.priceDeliveryServiceOnClient ? Number(details.priceDeliveryServiceOnClient).toFixed(2) : "0.00"} {t("currency")}
                </span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                <span style={{ fontSize: "14px", color: token.colorTextSecondary }}>{t("delivery_type")}:</span>
                <Tag
                  title={details?.isExpressDelivery ? t("express_delivery") : t("normal_delivery")}
                  color={details?.isExpressDelivery ? "blue" : "default"}
                />
              </div>

              <div style={{
                borderTop: `2px solid ${token.colorSuccess}`,
                marginTop: "16px",
                paddingTop: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span style={{ fontSize: "16px", fontWeight: "600", color: token.colorText }}>{t("total_amount_received")}:</span>
                <span style={{ fontSize: "20px", fontWeight: "700", color: token.colorSuccess }}>
                  {details?.finalPrice ? Number(details.finalPrice).toFixed(2) : "0.00"} {t("currency")}
                </span>
              </div>
            </div>
          </div>
        </Modal>
      </PageDetails>
    </>
  );
};

const OrderDetailesPage = () => {
  return (
    <OrderContextProvider>
      <OrderStatusContextProvider>
        <OrderHistoryContextProvider>
          <DeliveryWorkerHistoryContextProvider>
            <StoreContextProvider>
              <Order />
            </StoreContextProvider>
          </DeliveryWorkerHistoryContextProvider>
        </OrderHistoryContextProvider>
      </OrderStatusContextProvider>
    </OrderContextProvider>
  );
};
export default OrderDetailesPage;

//TODO

// "orderProducts": [],
// "reasonCancel": null
